import {
  useListingComparableSetForAgentQuery,
  useListingComparableSetForReportTokenQuery,
  useUpdateListingComparableSetForAgentMutation,
} from '@client/graphql/__generated__/main-operations';
import {
  ListingComparableSetSource,
  PropertyDetailsFragment,
  PublicListingFragment,
  UpdateListingComparableInput,
} from '@client/graphql/__generated__/types';
import isEqualWith from 'lodash/isEqualWith';
import { useEffect, useMemo, useState } from 'react';
import { ComparableState } from '~/apps/consumer/pages/ListingSetup/Comparables/ComparablesPage';

export const useListingReportComps = ({
  listing,
  propertyDetails,
  isViewOnly,
  token,
}: {
  listing?: PublicListingFragment;
  propertyDetails?: PropertyDetailsFragment;
  isViewOnly?: boolean;
  token?: string;
}) => {
  const [comps, setComps] = useState<ComparableState[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const [updateComparableSet] = useUpdateListingComparableSetForAgentMutation();

  const {
    data: comparableSetData,
    loading: comparableSetLoading,
    refetch: refetchComparableSet,
  } = useListingComparableSetForAgentQuery({
    variables: {
      input: {
        listingId: listing?.id || '',
        propertyDetailsId: propertyDetails?.id || '',
      },
    },
    skip: (!listing && !propertyDetails) || !!token,
  });

  const { data: tokenComparableSetData, loading: tokenComparableSetLoading } =
    useListingComparableSetForReportTokenQuery({
      variables: {
        input: {
          token: token || '',
          listingId: listing?.id || '',
          propertyDetailsId: propertyDetails?.id || '',
        },
      },
      skip: !token || (!listing && !propertyDetails),
    });

  const comparableSet = useMemo(() => {
    if (token) {
      return tokenComparableSetData?.listingComparableSetForReportToken;
    }

    return comparableSetData?.listingComparableSetForAgent;
  }, [token, comparableSetData, tokenComparableSetData]);

  const updateComps = async (comparables: UpdateListingComparableInput[]) => {
    if (isViewOnly || (!listing && !propertyDetails)) {
      return;
    }

    await updateComparableSet({
      variables: {
        input: {
          comparables,
          listingId: listing?.id || '',
          propertyDetailsId: propertyDetails?.id || '',
        },
      },
    });
    await refetchComparableSet();
  };

  const originalOrderedComps = useMemo(() => {
    if (comparableSet?.listingComparables) {
      return comparableSet.listingComparables
        ?.toSorted((a, b) => {
          // NOTE: System comparables should always be at the start of the list
          if (
            a.source === ListingComparableSetSource.SYSTEM &&
            b.source !== ListingComparableSetSource.SYSTEM
          )
            return -1;
          if (
            a.source !== ListingComparableSetSource.SYSTEM &&
            b.source === ListingComparableSetSource.SYSTEM
          )
            return 1;

          return a.order - b.order;
        })
        ?.map((comp) => ({
          mlsListing: comp.mlsListing,
          source: comp.source,
        }));
    }

    return [];
  }, [comparableSet]);

  // sets the comps on initial page load
  useEffect(() => {
    if (!isDirty && originalOrderedComps?.length) {
      setComps(originalOrderedComps || []);
    }
  }, [isDirty, originalOrderedComps]);

  // updates the dirty state based on changes to the comps compared to the original ordered comps
  useEffect(() => {
    if (isViewOnly) {
      return;
    }

    const comparator = (a: ComparableState[], b: ComparableState[]) =>
      a.length === b.length &&
      a.every(
        (comparable, index) =>
          comparable.mlsListing.id === b[index].mlsListing.id
      );
    if (!isEqualWith(comps, originalOrderedComps, comparator)) {
      setIsDirty(true);
    } else {
      setIsDirty(false);
    }
  }, [comps]); // eslint-disable-line react-hooks/exhaustive-deps

  // updates the comps in the database when the comps are dirty
  useEffect(() => {
    if (isDirty && !isViewOnly) {
      const input = comps.map((comp, index) => ({
        mlsListingId: comp.mlsListing.id,
        order: index + 1,
        source: comp.source,
      }));
      void updateComps(input);
    }
  }, [isDirty, comps, isViewOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    comps,
    setComps,
    isDirty,
    setIsDirty,
    updateComps,
    comparableSetLoading: tokenComparableSetLoading || comparableSetLoading,
    refetchComparableSet,
    comparableSet,
  };
};
